import React from 'react'
import styled from '../../constants/theme'
import { FormElementProps } from './FormElementParts'

const RadioElement: React.FC<FormElementProps> = ({
  disabled,
  label,
  name,
  onChange,
  options,
  value,
}) => {
  const elRef = React.useRef<HTMLInputElement>(null)
  const callChange = React.useCallback(
    val => {
      if (onChange) {
        onChange({ name, value: val })
      }
    },
    [name, onChange],
  )
  const handleChange = e => {
    e.preventDefault()
    callChange(e.target.value)
  }
  React.useEffect(() => {
    if (elRef.current) {
      callChange(elRef.current.value)
    }
  }, [callChange])
  if (!options || !options.length) {
    return null
  }
  return (
    <React.Fragment>
      <GroupLabel
        id={`${name}Label`}
        dangerouslySetInnerHTML={{
          __html: label,
        }}
      />
      <Options aria-labelledby={`${name}Label`} role="radiogroup">
        {options.map((option, index) => {
          const key = `${name}_option_${option.value}_${index}`
          const checked = `${value}` === `${option.value}`
          return (
            <Radio key={key}>
              <RadioInput
                aria-checked={checked}
                checked={checked}
                className={checked ? 'isChecked' : undefined}
                disabled={disabled}
                id={key}
                name={name}
                onChange={handleChange}
                style={{ opacity: 0 }}
                type="radio"
                value={option.value}
              />
              <Label
                className={checked ? 'isChecked' : undefined}
                htmlFor={key}
              >
                {option.label}
              </Label>
            </Radio>
          )
        })}
      </Options>
    </React.Fragment>
  )
}

const RadioInput = styled.input({
  '&:disabled': {
    cursor: 'not-allowed',
  },
  '&:hover': {
    cursor: 'pointer',
  },
  height: '100%',
  left: 0,
  opacity: 0,
  position: 'absolute',
  top: 0,
  width: 16,
  zIndex: 1,
})

const GroupLabel = styled.div(
  {
    fontWeight: 'bold',
    marginBottom: 20,
  },
  ({ theme }) => ({
    color: theme.text,
    fontFamily: theme.fontFamily,
  }),
)

const Radio = styled.li(
  {
    display: 'inline-block',
    height: 26,
    lineHeight: 1.1,
    position: 'relative',
    width: '100%',
  },
  ({ theme }) => ({
    color: theme.text,
    fontFamily: theme.fontFamily,
  }),
)

const Label = styled.label(
  {
    '&:hover:after': {
      cursor: 'pointer',
    },
    '&:after': {
      content: '""',
      height: 28,
      left: -6,
      position: 'absolute',
      top: -6,
      width: 28,
    },
    '&:before': {
      borderRadius: '50%',
      content: '""',
      display: 'inline-block',
      height: 16,
      left: 0,
      position: 'absolute',
      top: 1,
      width: 16,
    },
    '&:hover:not(.isChecked)': {
      cursor: 'pointer',
    },
    paddingLeft: 40,
    position: 'relative',
  },
  ({ theme }) => ({
    '&.isChecked:before': {
      backgroundColor: theme.primary,
      border: `1px solid ${theme.primary}`,
    },
    '&:before': {
      border: `1px solid ${theme.primaryDark}`,
    },
  }),
)

const Options = styled.div({ marginLeft: 8 })

export default RadioElement
